import type { AxiosError, AxiosRequestConfig } from "axios";
import { axiosApiInstance } from "../axios";
import {
  QUICKBOOKS_OPERATION_STATUSES,
  QuickBooksOperation,
  QuickBooksOperationApiError,
  QuickBooksOperationBatch,
  QuickBooksOperationCapabilities,
  SunShipmentOperations,
} from "../types/quickBooksOperations";

class OperationApiError extends Error implements QuickBooksOperationApiError {
  status?: number;
  code?: string;
  retryAfter?: number;
  isNetworkError: boolean;
  isCanceled: boolean;
  body?: { code?: string; message?: string; [key: string]: unknown };

  constructor(message: string, options: Partial<QuickBooksOperationApiError> = {}) {
    super(message);
    this.name = "QuickBooksOperationApiError";
    this.status = options.status;
    this.code = options.code;
    this.retryAfter = options.retryAfter;
    this.isNetworkError = options.isNetworkError ?? false;
    this.isCanceled = options.isCanceled ?? false;
    this.body = options.body;
  }
}

function errorFrom(error: unknown): OperationApiError {
  const candidate = error as { code?: string; name?: string; isAxiosError?: boolean };
  if (candidate?.code === "ERR_CANCELED" || candidate?.name === "CanceledError") {
    return new OperationApiError("Request canceled", { isCanceled: true });
  }
  if (!candidate?.isAxiosError && !(candidate && "response" in candidate)) {
    return new OperationApiError("Unable to reach the QuickBooks operation service", {
      isNetworkError: true,
    });
  }

  const axiosError = error as AxiosError;
  const body = (axiosError.response?.data || {}) as {
    code?: string;
    message?: string;
    [key: string]: unknown;
  };
  const retryAfterHeader = axiosError.response?.headers?.["retry-after"];
  const retryAfter = retryAfterHeader ? Number(retryAfterHeader) : undefined;
  return new OperationApiError(
    body.message || axiosError.message || "Unable to reach the QuickBooks operation service",
    {
      status: axiosError.response?.status,
      code: body.code,
      retryAfter: Number.isFinite(retryAfter) ? retryAfter : undefined,
      isNetworkError: !axiosError.response,
      body,
    },
  );
}

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response = await axiosApiInstance.request<T>({
      timeout: 15000,
      ...config,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken") || ""}`,
        "content-type": "application/json",
        ...config.headers,
      },
    });
    return response.data;
  } catch (error) {
    throw errorFrom(error);
  }
}

function validateOperation(value: QuickBooksOperation): QuickBooksOperation {
  if (
    !value ||
    typeof value.operation_id !== "string" ||
    value.account !== "sun" ||
    !["shipment", "sun_order"].includes(value.source_type) ||
    typeof value.source_id !== "number" ||
    !["purchase_order", "invoice"].includes(value.document_type) ||
    typeof value.generation !== "number" ||
    !QUICKBOOKS_OPERATION_STATUSES.includes(value.status) ||
    typeof value.can_retry !== "boolean" ||
    typeof value.can_replace !== "boolean"
  ) {
    throw new OperationApiError("QuickBooks returned an invalid operation status", {
      code: "invalid_response",
    });
  }
  if (value.status === "succeeded" && !value.quickbooks_id) {
    throw new OperationApiError("QuickBooks returned a completed operation without an ID", {
      code: "invalid_response",
    });
  }
  return value;
}

function validateBatch(value: QuickBooksOperationBatch): QuickBooksOperationBatch {
  if (!value || typeof value.batch_id !== "string" || !Array.isArray(value.operations)) {
    throw new OperationApiError("QuickBooks returned an invalid operation batch", {
      code: "invalid_response",
    });
  }
  return { ...value, operations: value.operations.map(validateOperation) };
}

export const quickBooksOperationsApi = {
  getCapabilities: (signal?: AbortSignal) =>
    request<QuickBooksOperationCapabilities>({
      method: "GET",
      url: "quickbooks/operations/capabilities",
      signal,
    }).then((value) => {
      if (!value || typeof value.sun !== "boolean" || typeof value.solmaz !== "boolean") {
        throw new OperationApiError("QuickBooks returned invalid capabilities", {
          code: "invalid_response",
        });
      }
      return value;
    }),

  enqueueShipment: (shipmentId: number, signal?: AbortSignal) =>
    request<QuickBooksOperationBatch>({
      method: "POST",
      url: "quickbooks/sun/shipment-operations",
      data: { shipment_id: shipmentId, documents: ["purchase_order", "invoice"] },
      signal,
    }).then(validateBatch),

  enqueueSunOrderInvoice: (sunOrderId: number, signal?: AbortSignal) =>
    request<QuickBooksOperation>({
      method: "POST",
      url: "quickbooks/sun/order-invoice-operations",
      data: { sun_order_id: sunOrderId },
      signal,
    }).then(validateOperation),

  getOperation: (operationId: string, signal?: AbortSignal) =>
    request<QuickBooksOperation>({
      method: "GET",
      url: `quickbooks/operations/${encodeURIComponent(operationId)}`,
      signal,
    }).then(validateOperation),

  getBatch: (batchId: string, signal?: AbortSignal) =>
    request<QuickBooksOperationBatch>({
      method: "GET",
      url: `quickbooks/batches/${encodeURIComponent(batchId)}`,
      signal,
    }).then(validateBatch),

  getLatestShipment: (shipmentId: number, signal?: AbortSignal) =>
    request<SunShipmentOperations>({
      method: "GET",
      url: `quickbooks/sun/shipments/${encodeURIComponent(shipmentId)}/operations`,
      signal,
    }).then((value) => {
      if (
        !value ||
        value.source_type !== "shipment" ||
        value.source_id !== shipmentId ||
        !Array.isArray(value.operations)
      ) {
        throw new OperationApiError("QuickBooks returned invalid shipment operations", {
          code: "invalid_response",
        });
      }
      return { ...value, operations: value.operations.map(validateOperation) };
    }),

  getLatestSunOrder: (sunOrderId: number, signal?: AbortSignal) =>
    request<QuickBooksOperation | null>({
      method: "GET",
      url: `quickbooks/sun/orders/${encodeURIComponent(sunOrderId)}/invoice-operation`,
      signal,
    }).then((value) => (value === null ? null : validateOperation(value))),

  retry: (operationId: string, signal?: AbortSignal) =>
    request<QuickBooksOperation>({
      method: "POST",
      url: `quickbooks/operations/${encodeURIComponent(operationId)}/retry`,
      signal,
    }).then(validateOperation),

  replace: (operationId: string, signal?: AbortSignal) =>
    request<QuickBooksOperation>({
      method: "POST",
      url: `quickbooks/operations/${encodeURIComponent(operationId)}/replace`,
      signal,
    }).then(validateOperation),
};

export { OperationApiError };
export default quickBooksOperationsApi;
